import {
  MyQuestCarouselItem,
  MyQuestCarouselNextArrow,
  MyQuestCarouselPrevArrow,
  MyQuestStickersCard,
  MyQuestStreakCard
} from '@components/MyQuest'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { Flex, Row } from 'antd'
import { useMemo } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { Link } from '@suntex.core/nav'
import { getFlags } from '@suntex.fim/flags'
import { useFramework } from '@suntex.fim/framework'
import { usePlayerContext, usePlayerProgressSummary } from '@suntex.fim/player'
import { LoaderOverlay } from '@suntex.fim/ui'

// Constants
const QUEST_GUTTER = 30
const STICKER_TARGET = 200
const TEN = 10
const MAX_PERCENTAGE = 100
const CAROUSEL_SETTINGS = {
  className: 'center',
  infinite: true,
  centerPadding: '60px',
  slidesToShow: 4,
  slidesToScroll: 1,
  swipeToSlide: true,
  variableWidth: true,
  adaptiveHeight: false,
  arrows: true,
  dots: false
} as const

// Helper function to calculate dynamic streak target
const calculateStreakTarget = (currentStreak: number): number => {
  // Always set target to the next multiple of 10
  return Math.ceil((currentStreak + 1) / TEN) * TEN
}

function MyQuests() {
  const { api } = useFramework()
  const { playerId } = usePlayerContext()
  const playerProgress = usePlayerProgressSummary()

  const { data: questItems = [], isLoading } = useQuery({
    queryKey: ['player', 'quests', playerId],
    queryFn: async () => {
      const response = await api.get_Player_Quests({
        playerId: playerId,
        includeCompletedQuests: false
      })
      return response.questItems
    },
    enabled: playerId > 0
  })

  // Memoized calculations
  const earnedStickers = useMemo(() => {
    return playerProgress.stickers - playerProgress.stickers1W
  }, [playerProgress])

  const currentStreak = useMemo(() => {
    return playerProgress.currentStreak
  }, [playerProgress])

  const streakTarget = useMemo(() => {
    return calculateStreakTarget(currentStreak)
  }, [currentStreak])

  // Slider settings with memoized arrows
  const sliderSettings = useMemo(
    () => ({
      ...CAROUSEL_SETTINGS,
      nextArrow: <MyQuestCarouselNextArrow />,
      prevArrow: <MyQuestCarouselPrevArrow />
    }),
    []
  )

  if (isLoading) {
    return <LoaderOverlay isLoading={true} />
  }

  return (
    <Flex vertical gap={40} className="my-quest-container">
      <Flex justify="space-between" className="my-quest" wrap gap={10}>
        <h3>You have new quests to complete! Let&apos;s get to it.</h3>
        <Button>
          <Link className="my-quest-btn" to={'/player/my-badges'}>
            Go to My Badges
          </Link>
        </Button>
      </Flex>
      <Row gutter={[QUEST_GUTTER, QUEST_GUTTER]}>
        {getFlags().player.streaks && (
          <MyQuestStreakCard
            currentStreak={currentStreak}
            streakTarget={streakTarget}
            maxPercentage={MAX_PERCENTAGE}
            step={TEN}
          />
        )}
        <MyQuestStickersCard
          earnedStickers={earnedStickers}
          stickerTarget={STICKER_TARGET}
          maxPercentage={MAX_PERCENTAGE}
          step={TEN}
        />
      </Row>
      <Flex vertical gap={30} className="carousel-container">
        <h3>My Quests</h3>
        {questItems.length > 0 ? (
          <Slider {...sliderSettings} className="my-quest-carousel">
            {questItems.map((quest) => (
              <MyQuestCarouselItem key={quest.id} quest={quest} />
            ))}
          </Slider>
        ) : (
          <p>No quests available at the moment.</p>
        )}
      </Flex>
    </Flex>
  )
}

export default MyQuests
